import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth.js';
import { Layout } from './components/Layout.js';
import { Landing } from './pages/Landing.js';
import { Login } from './pages/Login.js';
import { Dashboard } from './pages/Dashboard.js';
import { Mailboxes } from './pages/Mailboxes.js';
import { Contacts } from './pages/Contacts.js';
import { CampaignBuilder } from './pages/CampaignBuilder.js';
import { Inbox } from './pages/Inbox.js';
import { Skeleton } from './components/ui.js';

// Deliverability area is lazy-loaded so Recharts stays off the main bundle.
const Reports = lazy(() =>
  import('./pages/Reports.js').then((m) => ({ default: m.Reports })),
);
const SenderHealth = lazy(() =>
  import('./pages/SenderHealth.js').then((m) => ({ default: m.SenderHealth })),
);
const Warmup = lazy(() =>
  import('./pages/Warmup.js').then((m) => ({ default: m.Warmup })),
);

function RouteFallback() {
  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-56" />
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Skeleton className="h-28 rounded-2xl" />
        <Skeleton className="h-28 rounded-2xl" />
        <Skeleton className="h-28 rounded-2xl" />
        <Skeleton className="h-28 rounded-2xl" />
      </div>
      <Skeleton className="h-72 rounded-2xl" />
    </div>
  );
}

export function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-canvas flex min-h-screen items-center justify-center font-mono text-xs uppercase tracking-widest text-slate-400">
        Loading…
      </div>
    );
  }

  return (
    <BrowserRouter>
      {user ? (
        <Layout>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/campaigns/new" element={<CampaignBuilder />} />
              <Route path="/campaigns/:id/edit" element={<CampaignBuilder />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/mailboxes" element={<Mailboxes />} />
              <Route path="/deliverability/reports" element={<Reports />} />
              <Route
                path="/deliverability/health"
                element={<SenderHealth />}
              />
              <Route path="/deliverability/warmup" element={<Warmup />} />
              {/* Deliverability landing → the account health overview (spec §6). */}
              <Route
                path="/deliverability"
                element={<Navigate to="/deliverability/health" replace />}
              />
              {/* Authed users hitting /login (or anything else) land in the app. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Layout>
      ) : (
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          {/* Unknown paths for logged-out users go to the marketing page. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </BrowserRouter>
  );
}
